import React, { useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSet } from 'react-use';
import { Accordion } from '../../../../../../../CommonComponents/OPL/Accordion/Accordion/Accordion';
import { AccordionButton } from '../../../../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Checkbox } from '../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Separator } from '../../../../../../../CommonComponents/OPL/Separator/Separator';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { ChangeStateOptions } from '../../../../api/useWhatIfOptionsQuery';
import { StyledAccordionPanel } from '../../WhatIf.styles';
import { DEFAULT_STATE_CHANGE, FormValues } from './ChangeState.meta';

const AT_RETIREMENT_VALUE = 'at-retirement';

interface Props {
  Title: string;
  options: ChangeStateOptions;
}

export const ChangeState = ({ Title, options }: Props) => {
  const [, { has: isStateChangeCollapsed, toggle: toggleStateChange }] = useSet<string | null>();

  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<FormValues>();

  const { fields, append: appendStateChange, remove: removeStateChange } = useFieldArray({
    control,
    name: 'StateChanges',
  });

  const watchStateChanges = watch('StateChanges');
  const iWontMove = watch('IWontMove');

  const stateChanges = fields.map((field, index) => ({
    ...field,
    ...watchStateChanges[index],
  }));

  const hasIncompleteEditableStateChange = stateChanges.some(
    stateChange => !stateChange.IsApplied && stateChange.NewFilingState == null,
  );

  const filingStateOptions = useMemo(
    () => [
      {
        label: 'United States',
        options: options.filingStateOptions.filter(({ isInsideUS }) => isInsideUS),
      },
      {
        label: 'Outside U.S.',
        options: options.filingStateOptions.filter(({ isInsideUS }) => !isInsideUS),
      },
    ],
    [options.filingStateOptions],
  );

  const flatFilingStateOptions = useMemo(
    () => filingStateOptions.flatMap(({ options: opts }) => opts),
    [filingStateOptions],
  );

  const yearOptions = useMemo(
    () => [
      { label: 'At Retirement', value: AT_RETIREMENT_VALUE },
      ...options.yearOptions.map(year => ({
        label: year.toString(),
        value: year,
      })),
    ],
    [options.yearOptions],
  );

  const getEffectiveYearValue = (effectiveAtRetirement: boolean, effectiveYear: number | null) => {
    if (effectiveAtRetirement) {
      return yearOptions.find(({ value }) => value === AT_RETIREMENT_VALUE);
    }

    if (effectiveYear != null) {
      return yearOptions.find(({ value }) => value === effectiveYear);
    }

    return undefined;
  };

  const handleEffectiveYearChange = (index: number, option: { value: string | number } | null) => {
    if (option?.value === AT_RETIREMENT_VALUE) {
      setValue(`StateChanges.${index}.EffectiveAtRetirement`, true);
      setValue(`StateChanges.${index}.EffectiveYear`, null);
      return;
    }

    setValue(`StateChanges.${index}.EffectiveAtRetirement`, false);
    setValue(
      `StateChanges.${index}.EffectiveYear`,
      typeof option?.value === 'number' ? option.value : null,
    );
  };

  return (
    <>
      <Text fontSize="h5" fontWeight={600} px="12" py="16">
        What-if...
      </Text>

      <Separator />

      <Box
        display="flex"
        flexDirection="column"
        p={12}
        css={`
          gap: ${({ theme }) => theme.space['16']}px;
        `}
      >
        <Text fontSize="h6" fontWeight={500}>
          {Title}
        </Text>

        <Text color="neutral.600">Current filing state: {options.currentFilingStateLabel}</Text>
      </Box>

      <Accordion
        isActive={false}
        css={`
          border: none;
          border-radius: 0;
          ${iWontMove
            ? `
            opacity: 0.55;
            pointer-events: none;
          `
            : ''}
        `}
      >
        {stateChanges.map((stateChange, index) => {
          const buttonId = `what-if-change-state--accordion-button--${stateChange.id}`;
          const panelId = `what-if-change-state--accordion-panel--${stateChange.id}`;
          const isReadOnly = stateChange.IsApplied || iWontMove;

          return (
            <React.Fragment key={stateChange.id}>
              <AccordionButton
                id={buttonId}
                panelId={panelId}
                isActive={!isStateChangeCollapsed(stateChange.id)}
                onClick={() => toggleStateChange(stateChange.id)}
                css={
                  isReadOnly
                    ? ({ theme }) => `
                        background: ${theme.colors.neutral[100]} !important;
                        color: ${theme.colors.neutral[600]};

                        &:hover {
                          background: ${theme.colors.neutral[100]} !important;
                        }

                        &::before {
                          color: ${theme.colors.neutral[400]};
                        }
                      `
                    : undefined
                }
                extra={
                  !isReadOnly && (
                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => removeStateChange(index)}
                    />
                  )
                }
              >
                {`State change ${index + 1}`}
              </AccordionButton>

              <StyledAccordionPanel
                id={panelId}
                buttonId={buttonId}
                isActive={!isStateChangeCollapsed(stateChange.id)}
              >
                <Box display="flex" flexDirection="column" gridGap="16" px="16" pb="32">
                  <Controller
                    control={control}
                    name={`StateChanges.${index}.NewFilingState`}
                    render={({ field }) => (
                      <Select
                        label="Filing State"
                        placeholder="Select filing state"
                        value={flatFilingStateOptions.find(({ value }) => value === field.value)}
                        options={filingStateOptions}
                        onChange={opt => field.onChange(opt ? opt.value : null)}
                        formVariant={
                          errors.StateChanges?.[index]?.NewFilingState ? 'error' : undefined
                        }
                        helperText={errors.StateChanges?.[index]?.NewFilingState?.message}
                        disabled={isReadOnly}
                        isPortal
                      />
                    )}
                  />

                  <Select
                    label="Effective Year"
                    placeholder="Select effective year"
                    value={getEffectiveYearValue(
                      stateChange.EffectiveAtRetirement,
                      stateChange.EffectiveYear,
                    )}
                    options={yearOptions}
                    onChange={opt => handleEffectiveYearChange(index, opt)}
                    formVariant={errors.StateChanges?.[index]?.EffectiveYear ? 'error' : undefined}
                    helperText={errors.StateChanges?.[index]?.EffectiveYear?.message}
                    disabled={isReadOnly}
                    isPortal
                  />
                </Box>
              </StyledAccordionPanel>
            </React.Fragment>
          );
        })}
      </Accordion>

      {!iWontMove && !hasIncompleteEditableStateChange && (
        <Button
          variant="link"
          leftIcon="fas fa-plus"
          onClick={() => appendStateChange(DEFAULT_STATE_CHANGE)}
          my="24"
          ml="16"
        >
          {`Add ${stateChanges.length > 0 ? 'another ' : ''}state change`}
        </Button>
      )}
      <Box px="16" pb="24" pt={iWontMove || hasIncompleteEditableStateChange ? '16' : '0'}>
        <Checkbox {...register('IWontMove')}>I won't move at all</Checkbox>
      </Box>
    </>
  );
};
